import type { ReactNode } from 'react'
import { Icon, type IconName } from './Icon'

interface NoticeProps {
  tone?: 'warning' | 'error'
  icon?: IconName
  title: string
  children?: ReactNode
}

export function Notice({ tone = 'warning', icon = 'info', title, children }: NoticeProps) {
  return (
    <div className={`notice${tone === 'error' ? ' notice--error' : ''}`} role="status">
      <span className="notice__icon">
        <Icon name={icon} size={22} />
      </span>
      <div>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  )
}

/**
 * Dutch names for the section keys the API puts in `unavailableSections`.
 *
 * These are the literal strings passed to `Section(...)` in the backend's `CarReportService` — a
 * dotted path into the report document, not the C# property name. Copy them verbatim when the
 * backend adds one; guessing the casing means every lookup misses and the user is shown a raw key
 * like `technical.axles` in the middle of a Dutch sentence.
 *
 * Anything not in this map falls through to the key itself rather than being dropped: a section the
 * frontend has not heard of is still a section the user is missing.
 */
const SECTION_NAMES: Record<string, string> = {
  powertrain: 'motor- en brandstofgegevens',
  'powertrain.gearbox': 'transmissiegegevens',
  'powertrain.engine': 'motorgegevens',
  'technical.axles': 'asgegevens',
  'technical.dimensions': 'maten en gewichten',
  'identity.bodyTypes': 'carrosseriegegevens',
  'identity.vehicleClasses': 'voertuigklassen',
  'identity.manufacturerModelNames': 'de modelnamen van de fabrikant',
  'identity.typeApprovalWithdrawal': 'de status van de typegoedkeuring',
  addedEquipment: 'toegevoegde objecten',
  'inspection.history': 'de APK-historie',
  'inspection.defects': 'APK-bevindingen',
  'inspection.defectDescriptions': 'omschrijvingen van APK-bevindingen',
  recalls: 'terugroepacties',
  'recalls.details': 'details van terugroepacties',
  'recalls.risks': 'de risico-inschatting van terugroepacties',
  'recalls.notifications': 'informatie over eigenaarsmeldingen',
  'odometer.explanation': 'de toelichting op de tellerstandcontrole',
  review: 'de review van dit model',
  electric: 'de accu- en laadgegevens van dit model',
  'financial.ownership.energy': 'de actuele energieprijzen',
}

/**
 * Tells the user that a part of the report is missing because a dataset could not be read — not
 * because the car has nothing to show there. The API distinguishes the two deliberately, and
 * throwing that away at the last step would make the report quietly dishonest.
 */
export function UnavailableSections({ sections }: { sections: string[] }) {
  if (sections.length === 0) {
    return null
  }

  const names = sections.map((section) => SECTION_NAMES[section] ?? section)

  return (
    <Notice title="Een deel van de gegevens is nu niet beschikbaar" icon="warning">
      <p>
        We konden {names.join(', ')} op dit moment niet ophalen. Dit betekent niet dat deze auto ze
        niet heeft. Probeer het later nog eens.
      </p>
    </Notice>
  )
}
